import { PlusIcon } from '@heroicons/react/outline';
import { Affix, Skeleton, Typography } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { Montserrat } from 'next/font/google';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { formatPrice } from '@/utils/Product';
import { programmaticScrollTo } from '@/utils/ScrollControl/scrollControl';

import { cardVariants, formatCartData, right } from '../CartPage';
import CartContactForm from '../CartPage/CartContactForm';
import Step_2_OrderCard from '../CartPage/Step_2_OrderCard';
import { Category, formatItem, Type } from '../CartPage/Step_3_orderCard';
import { ShouldRender } from '../shared/basic/ShouldRender';
import { formateErrorObject } from '../shared/formateErrorObject';
import { getLoginToken, getUserId } from '../shared/getToken';
import { notify } from '../shared/notify';
import { tokenUpdate } from '../../atoms/authModalControl';
import { cartAtom } from '../../atoms/Cart';
import { localUserID, user } from '../../atoms/user';
import appwriteService from '../../config/appwrite';
import { useAuth } from '../../hooks/useAuth';
import {
  addCouponCode,
  getCartDetails,
  getCartSummary,
  handleRemoveCoupon,
} from '../../services/Cart';
import { CartItem } from '../../utils/interfaces/cart';
import { User } from '../../utils/interfaces/User';
import { BookDummy } from '../../../assets';

const montserrat = Montserrat({
  subsets: ['latin'],
  fallback: ['Segoe UI', 'Roboto', 'Arial', 'sans-serif'],
  display: 'swap',
});
const Checkout = () => {
  const [amount, setAmount] = useState<{
    subTotal: number;
    discount: string;
    total: number;
    tax: number;
    couponCode: { coupon_code: string }[];
    actualCartTotal: number;
    currency: string;
    currencyCode: string;
  }>({
    subTotal: 0,
    discount: '',
    total: 0,
    tax: 0,
    actualCartTotal: 0,
    currency: '',
    currencyCode: '',
    couponCode: [{ coupon_code: '' }],
  });
  const [products, setProducts] = useState<CartItem[]>([]);

  const [cartDataLoading, setCartDateLoading] = useState<boolean>(true);
  const [loggedIn, setLoggedIn] = useState<boolean>(false);
  const [, setCartData] = useRecoilState(cartAtom);

  const [token, setToken] = useState('');
  const [removeLoading, setRemoveLoading] = useState(false);
  const [isTokenChanged, setIsTokenChanged] = useRecoilState(tokenUpdate);
  const [couponMessage, setCouponMessage] = useState({
    message: '',
    type: '',
  });
  const [synced, setSynced] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);
  const [couponApply, setCouponApply] = useState<{
    value: boolean;
    isSuccess: boolean;
  }>({ value: false, isSuccess: false });
  const userData: User | undefined = useRecoilValue(user);
  const screens = useBreakpoint();
  const { syncCart } = useAuth();
  const router = useRouter();
  const { redirect_url, cartId, pre } = router.query;
  const localUserId = useRecoilValue(localUserID);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [wrapInAffix, setWrapInAffix] = useState(false);
  const cartData = useRecoilValue(cartAtom);

  const handleScrollToBottom = () => {
    if (typeof window !== 'undefined') {
      programmaticScrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const fetchData = async () => {
    try {
      const token = await getLoginToken();
      setToken(token as string);
    } catch (error) {
      console.error('Error fetching token:', error);
    }
  };

  const handleCartDetails = async () => {
    try {
      const data = await getCartDetails(
        localUserId.toString(),
        (cartData[0]?.cartId as number) || Number(cartId),
      );

      const formattedData = formatCartData(
        data?.data?.[0]?.cart_items,
        data?.currency_code,
        data?.cart_id,
      );
      setCartData(formattedData);
      setProducts(formattedData);
      if (data?.data?.[0]?.cart_items?.length > 0 && cartId) {
        await handleCartSummary();
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    }
  };

  const removeCoupon = async (coupon_code: string) => {
    try {
      setRemoveLoading(true);
      await handleRemoveCoupon(
        coupon_code,
        (cartData[0]?.cartId as number) || Number(cartId),
      );
      await handleCartSummary();
    } catch (error: any) {
      const message = formateErrorObject(error);
      setCouponMessage({
        message: message,
        type: 'error',
      });
    } finally {
      setRemoveLoading(false);
    }
  };

  useEffect(() => {
    let timeoutId: any;

    if (couponMessage.message !== '') {
      timeoutId = setTimeout(() => {
        setCouponMessage({
          message: '',
          type: '',
        });
      }, 3000);
    }

    return () => clearTimeout(timeoutId);
  }, [couponMessage.message]);

  useEffect(() => {
    fetchData();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [isTokenChanged]);

  const handleCartSummary = async () => {
    try {
      setCartDateLoading(true);
      const data = await getCartSummary(
        (cartData[0]?.cartId as number) || Number(cartId),
      );
      setAmount({
        subTotal: data?.sub_total || 0,
        discount: data?.discount || '',
        total: data?.grand_total || 0,
        currency: data?.currency_symbol || '₹',
        tax: data?.tax || 0,
        couponCode: data?.coupon_code || [],
        currencyCode: data?.currency_code || 'INR',
        actualCartTotal: data?.product_regular_price || 0,
      });
    } catch (error: any) {
      if (process.env.NEXT_PUBLIC_APP_ENV === 'development') {
        console.error('Error initializing messaging:', error);
      }
    } finally {
      setCartDateLoading(false);
    }
  };

  const handleAddCoupon = async (coupon_code: string) => {
    try {
      setCouponApply((prev) => ({
        ...prev,
        value: true,
      }));
      const message = await addCouponCode(coupon_code);
      setCouponApply((prev) => ({
        ...prev,
        isSuccess: true,
        value: false,
      }));
      setCouponMessage({
        message: message.message,
        type: 'success',
      });

      await handleCartSummary();
    } catch (error: any) {
      const message = formateErrorObject(error);
      setCouponMessage({
        message: message,
        type: 'error',
      });
    } finally {
      setCouponApply((prev) => ({
        ...prev,
        isSuccess: false,
        value: false,
      }));
    }
  };

  const handleSyncUser = async () => {
    try {
      const userId = await getUserId();
      await syncCart(
        userId as string,
        (cartData[0]?.cartId as number) || Number(cartId),
      );
    } catch (error) {
      console.error('Error fetching ', error);
    }
  };

  useEffect(() => {
    const fetchLoggedInStatus = async () => {
      const loggedInStatus = await appwriteService.isLoggedIn();
      setLoggedIn(loggedInStatus);
    };
    fetchLoggedInStatus();
    if (isTokenChanged) {
      setIsTokenChanged(false);
    }
  }, [appwriteService.account, isTokenChanged]);

  useEffect(() => {
    if (!(loggedIn && userData?.id && !synced)) {
      if (cartId) {
        handleCartDetails();
      }
    }
  }, [localUserId, redirect_url, cartId]);

  useEffect(() => {
    if (loggedIn && userData?.id && !synced && cartId) {
      handleSyncUser();
      setSynced(true);
    }
  }, [loggedIn, userData, synced, cartId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const checkHeight = () => {
      if (containerRef.current) {
        const containerHeight = containerRef.current.offsetHeight;
        const screenHeight = window.innerHeight;

        setWrapInAffix(containerHeight + 50 <= screenHeight);
      }
    };

    checkHeight();

    window.addEventListener('resize', checkHeight);

    return () => {
      window.removeEventListener('resize', checkHeight);
    };
  }, []);

  const ContainerContent = (
    <div className={montserrat.className}>
      <div
        ref={containerRef}
        className='lg:min-h-[93vh] col-span-1 border-borderDarkGray'
      >
        <div className='flex flex-col h-full'>
          <motion.div
            variants={cardVariants}
            initial='hidden'
            className='hidden lg:flex px-5 lg:px-0'
            animate='visible'
          >
            <div className='w-full'>
              <Step_2_OrderCard
                data={products}
                amount={amount}
                total={amount.total}
                handleAddCoupon={handleAddCoupon}
                removeCoupon={removeCoupon}
                removeLoading={removeLoading}
                couponMessage={couponMessage}
                loading={cartDataLoading}
                orderLoading={orderLoading}
                couponApplyLoading={couponApply}
                isPreOrder={pre ? true : false}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className=''>
        <div className='relative overflow-hidden px-4'>
          <div className='flex flex-col pt-2 md:pt-0 md:gap-y-2 lg:gap-y-5'>
            <ShouldRender check={!screens.lg}>
              <div className='pb-5 md:pb-4 md:pl-8 md:pr-8 lg:pr-0'>
                {/* <ShouldRender check={!screens.md && products?.length}>
                  <ShouldRender check={products[0]?.type !== 'digital'}>
                    <div className='bg-[#2C2302] p-2 md:p-3  rounded-lg  mb-2'>
                      <span className='font-bold text-xs md:text-sm p-1 md:tracking-wider text-white gap-x-1'>
                        Shipping cost:
                        <span className='inline-flex items-baseline '>
                          <ShouldRender
                            check={
                              getGlobalCountry()?.name?.toLowerCase() ===
                              'india'
                            }
                          >
                            <b className='line-through px-1 font-normal'>
                              ₹199
                            </b>
                          </ShouldRender>
                          <b className='text-[#FFC700] font-normal'>
                            Free Shipping
                          </b>
                        </span>
                      </span>
                    </div>
                  </ShouldRender>
                </ShouldRender> */}
                <div className=' bg-readerLightGrayBg px-4 py-3 rounded-lg '>
                  <ShouldRender check={!cartDataLoading && products[0]?.name}>
                    <div className=' gap-x-3.5 rounded-lg mb-1 md:mb-2 flex '>
                      <div
                        className={clsx(
                          products[0]?.type === 'hardcover' &&
                            products[0]?.itemType?.toUpperCase() === 'BOOK'
                            ? 'flex-col-reverse'
                            : '',
                          'flex gap-y-1 ',
                        )}
                      >
                        <div className='flex flex-col gap-y-2'>
                          <div
                            className={clsx(
                              '   bg-black rounded-[4px] w-[80px] md:w-[100px] max-h-[56px] flex justify-center md:gap-x-1.5 px-3 pt-3',
                            )}
                          >
                            <span>
                              <Image
                                src={products[0]?.image || BookDummy}
                                alt='Product'
                                width={25}
                                height={37}
                                className={clsx(
                                  !(
                                    products[0]?.type === 'hardcover' &&
                                    products[0]?.itemType?.toUpperCase() ===
                                    'BOOK'
                                  )
                                    ? 'w-[30px]'
                                    : 'object-contain w-[30px]',
                                )}
                              />
                            </span>
                            <ShouldRender
                              check={
                                products[0]?.type === 'hardcover' &&
                                products[0]?.itemType?.toUpperCase() === 'BOOK'
                              }
                            >
                              <span
                                onClick={() => {
                                  notify(
                                    'Unlock the free copy after purchase',
                                    'info',
                                  );
                                }}
                                className='flex justify-end items-end pl-1 md:pl-0 relative '
                              >
                                <span className='md:px-1 md:pt-1 md:bg-[#434343] rounded-md'>
                                  <Image
                                    src={products[0]?.image || BookDummy}
                                    alt='Digital-Product'
                                    width={0}
                                    height={27}
                                    className='object-contain w-[22px]'
                                  />
                                </span>

                                <div className="absolute inset-0 before:content-[''] before:absolute before:inset-0 before:bg-gradient-to-t from-bgColor " />
                                <div className='absolute md:pl-1 pb-1 text-fadedWhite text-[5px]  leading-tight left-[5px] md:left-1 bottom-0.5'>
                                  <p className='flex justify-center  pr-2 items-center'>
                                    <PlusIcon className='w-2.5 h-2.5' />
                                  </p>{' '}
                                  DIGITAL COPY
                                </div>
                              </span>
                            </ShouldRender>
                          </div>
                        </div>
                        <ShouldRender
                          check={
                            products[0]?.itemType?.toUpperCase() === 'BOOK' &&
                            products[0]?.type === 'hardcover'
                          }
                        >
                          <div className='bg-[#430F11] rounded-md flex justify-center'>
                            <span className='font-bold  px-1 py-[2px] tracking-wider text-[8px] uppercase text-[#FF1D26] whitespace-nowrap'>
                              Only few left
                            </span>
                          </div>
                        </ShouldRender>
                      </div>

                      <div
                        className={clsx(
                          products[0]?.itemType?.toUpperCase() === 'BOOK' &&
                            products[0]?.type === 'hardcover'
                            ? ''
                            : '',
                          'flex justify-between  text-white items-center h-full gap-x-1.5 xs:gap-x-3 w-full',
                        )}
                      >
                        <div className='flex flex-col gap-y-1 xs:gap-y-2 py-0.5 h-full'>
                          <Typography.Title
                            level={2}
                            className='text-xs md:text-base leading-tight'
                          >
                            {products[0]?.name}
                          </Typography.Title>
                          <span className='font-medium text-[10px] text-white'>
                            {formatItem(
                              products[0]?.category as Category,
                              products[0]?.itemType as Type,
                            )?.toUpperCase()}
                          </span>
                        </div>
                        <div className='flex justify-end flex-col items-end'>
                          <Typography.Title
                            level={3}
                            className='font-light text-[12px] md:text-sm whitespace-nowrap  text-textGrayColor line-through '
                          >
                            {products[0]?.currency}{' '}
                            {formatPrice(products?.[0]?.actualPrice || '')}
                          </Typography.Title>
                          <div className='font-medium text-[14px] md:text-base text-white whitespace-nowrap'>
                            {products[0]?.currency}{' '}
                            {formatPrice(products[0]?.price || '')}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className='flex justify-between'>
                      <div className='flex justify-center min-w-[80px]'>
                        <div className='flex text-textGrayColor font-medium text-[10px]  md:text-xs gap-2'>
                          <span className=''>Qty. </span>
                          <span className=''>0{products[0]?.quantity}</span>
                        </div>
                      </div>
                      <div
                        onClick={handleScrollToBottom}
                        className='text-[10px] md:text-xs text-primary font-medium underline no-select cursor-pointer'
                      >
                        Have a Coupon code?
                      </div>
                    </div>
                  </ShouldRender>
                  <ShouldRender check={cartDataLoading}>
                    <div className='flex justify-between gap-x-4 w-full'>
                      <div className='flex gap-x-4 '>
                        {/* Avatar Skeleton */}
                        <Skeleton.Button
                          active
                          shape='square'
                          size='small'
                          className='rounded-md w-[52px] h-[52px]'
                        />
                        <div className='w-full flex flex-col'>
                          <span className='w-1/3'>
                            <Skeleton.Button
                              active
                              shape='square'
                              size='small'
                              className='h-3 w-full'
                            />
                          </span>

                          <Skeleton.Input
                            active
                            size='small'
                            style={{ width: '80%' }}
                            className='h-5'
                          />
                        </div>
                      </div>
                      <div className='text-sm flex  items-center text-textDarkGrayColor'>
                        <Skeleton.Button
                          active
                          size='small'
                          className='h-full py-2'
                          style={{ width: '100%' }}
                        />
                      </div>
                    </div>
                  </ShouldRender>
                </div>
              </div>
            </ShouldRender>

            <div className={clsx('grid w-full lg:grid-cols-3 relative')}>
              <div
                className={clsx(
                  products?.length ? 'col-span-2' : 'col-span-2',
                  'lg:border-r-2 border-borderDarkGray ',
                )}
              >
                <motion.div
                  variants={right}
                  initial='hidden'
                  className='flex flex-col gap-y-4'
                  animate='visible'
                >
                  <div>
                    <CartContactForm
                      setOrderLoading={setOrderLoading}
                      orderLoading={orderLoading}
                      amount={amount}
                      token={token}
                      setProduct={() => setProducts([])}
                    />
                  </div>
                </motion.div>
              </div>
              {wrapInAffix ? (
                <Affix offsetTop={80}>{ContainerContent}</Affix>
              ) : (
                ContainerContent
              )}
            </div>
          </div>
        </div>
      </div>

      <span className='lg:hidden flex pb-6 md:pb-10 lg:pb-0'>
        <Step_2_OrderCard
          data={products}
          amount={amount}
          total={amount.total}
          handleAddCoupon={handleAddCoupon}
          removeCoupon={removeCoupon}
          removeLoading={removeLoading}
          couponMessage={couponMessage}
          loading={cartDataLoading}
          orderLoading={orderLoading}
          couponApplyLoading={couponApply}
        />
      </span>
      {/* <CancelPurchasePrompt
        discount={`₹${amount?.actualCartTotal - parseFormattedNumber(amount?.total?.toString())}`}
        productId={products[0]?.product_id}
      /> */}
    </>
  );
};

export default Checkout;
